import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth, apiClient } from '@/App';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import AppPageIntro from '@/components/AppPageIntro';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  User,
  Bell,
  Shield,
  CreditCard,
  Crown,
  Check,
  ExternalLink,
  Wallet,
  Trash2,
  Baby,
  TrendingUp,
  Sparkles,
  Link2,
  Briefcase
} from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';

export default function Settings() {
  const { user, upgradeToPremium, logout } = useAuth();
  const location = useLocation();
  const [premiumOpen, setPremiumOpen] = useState(false);
  const [churchMode, setChurchMode] = useState(() => localStorage.getItem('app_profile') === 'church');
  const toggleChurchMode = (enabled) => {
    setChurchMode(enabled);
    localStorage.setItem('app_profile', enabled ? 'church' : 'household');
    window.dispatchEvent(new Event('app-profile-changed'));
    toast.success(enabled ? 'Kerkmodus ingeschakeld' : 'Gezinsmodus ingeschakeld');
  };
  const [notifications, setNotifications] = useState({
    contractAlerts: true,
    savingsGoals: true,
    weeklyReport: false,
    priceDrops: true
  });
  const [familyProfile, setFamilyProfile] = useState({
    children_count: 0,
    children_birth_years: [],
    single_parent_household: false,
    receives_kinderbijslag: false,
    kinderbijslag_quarterly: 0,
    receives_kindgebonden_budget: false,
    kindgebonden_budget_monthly: 0,
    kinderopvangtoeslag_monthly: 0,
    zorgtoeslag_monthly: 0,
    partner_income_monthly: 0,
    household_taxable_income_annual: 0,
    net_salary_monthly: 0,
    income_type: 'salary',
    payment_frequency: 'monthly',
    expected_payment_day: 25,
    holiday_allowance_month: 5,
    thirteenth_month_month: 12,
    holiday_allowance_percent: 8,
    has_thirteenth_month: false,
    thirteenth_month_amount: 0,
    fixed_allowances_monthly: 0,
    fixed_deductions_monthly: 0,
    bike_plan_monthly: 0,
    self_employed_tax_reserve_percent: 0,
    self_employed_vat_reserve_percent: 0,
    self_employed_buffer_percent: 0,
    vacation_days_per_year: 0,
    vacation_days_remaining: 0,
  });
  const [childrenYearsInput, setChildrenYearsInput] = useState('');
  const [scenarioPercent, setScenarioPercent] = useState(10);
  const [scenarioResult, setScenarioResult] = useState(null);
  const [resettingCsvImports, setResettingCsvImports] = useState(false);
  const [resettingAllTransactions, setResettingAllTransactions] = useState(false);
  const [monthlyBudgets, setMonthlyBudgets] = useState({
    boodschappen: '400',
    kleding: '100',
    uit_eten: '75',
    vaste_lasten: '1200',
    overig: '150',
  });
  const [budgetMonth, setBudgetMonth] = useState('');
  const [savingBudgets, setSavingBudgets] = useState(false);
  const [rulesMeta, setRulesMeta] = useState({
    active_year: '',
    available_years: [],
    rule_version: '',
  });
  const [bankAccounts, setBankAccounts] = useState([]);
  const [accountForm, setAccountForm] = useState({
    bank_name: '',
    account_name: '',
    iban: '',
    account_type: 'checking',
    balance: '0',
  });
  const [savingAccount, setSavingAccount] = useState(false);
  const [bankLinkStatus, setBankLinkStatus] = useState({
    configured: false,
    mode: 'sandbox',
    redirect_uri: '',
    connected_accounts: 0,
  });
  const [startingBankLink, setStartingBankLink] = useState(false);
  const [syncingBankLink, setSyncingBankLink] = useState(false);
  const [deleteAccountOpen, setDeleteAccountOpen] = useState(false);
  const [deleteAccountForm, setDeleteAccountForm] = useState({ password: '', confirmation: '' });
  const [deletingAccount, setDeletingAccount] = useState(false);
  const onboardingItems = [
    {
      key: 'account',
      title: 'Voeg je eerste bankrekening toe',
      description: 'Dit geeft je dashboard en transacties direct meer context.',
      done: bankAccounts.length > 0,
    },
    {
      key: 'family',
      title: 'Vul je gezin en tegemoetkomingen in',
      description: 'Handig voor realistische scenario’s en betere inzichten.',
      done: Number(familyProfile.children_count || 0) > 0 || Number(familyProfile.household_taxable_income_annual || 0) > 0,
    },
    {
      key: 'plan',
      title: 'Controleer je abonnement en meldingen',
      description: 'Zo weet je welke functies actief zijn en welke signalen je ontvangt.',
      done: Boolean(user),
    },
  ];
  const showSetupCard = onboardingItems.some((item) => !item.done);

  const loadFamilyProfile = async () => {
    try {
      const res = await apiClient.get('/family/profile');
      setFamilyProfile(res.data);
      setChildrenYearsInput((res.data.children_birth_years || []).join(', '));
    } catch (error) {
      console.error('Error loading family profile:', error);
    }
  };

  const loadRulesMeta = async () => {
    try {
      const res = await apiClient.get('/family/rules-meta');
      setRulesMeta(res.data);
    } catch (error) {
      console.error('Error loading rules meta:', error);
    }
  };

  const loadAccounts = async () => {
    try {
      const res = await apiClient.get('/accounts');
      setBankAccounts(res.data || []);
    } catch (error) {
      console.error('Error loading accounts:', error);
    }
  };

  const loadBankLinkStatus = async () => {
    try {
      const res = await apiClient.get('/banking/truelayer/status');
      setBankLinkStatus(res.data || {});
    } catch (error) {
      console.error('Error loading bank link status:', error);
    }
  };

  const loadBudgets = async () => {
    try {
      const res = await apiClient.get('/budgets/current');
      setBudgetMonth(res.data?.month || '');
      setMonthlyBudgets({
        boodschappen: String(res.data?.budgets?.boodschappen ?? '400'),
        kleding: String(res.data?.budgets?.kleding ?? '100'),
        uit_eten: String(res.data?.budgets?.uit_eten ?? '75'),
        vaste_lasten: String(res.data?.budgets?.vaste_lasten ?? '1200'),
        overig: String(res.data?.budgets?.overig ?? '150'),
      });
    } catch (error) {
      console.error('Error loading budgets:', error);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      await Promise.all([
        loadFamilyProfile(),
        loadRulesMeta(),
        loadAccounts(),
        loadBankLinkStatus(),
        loadBudgets(),
      ]);
    };
    loadData();
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('bank_connected') === '1') {
      const imported = Number(params.get('transactions_imported') || 0);
      const updated = Number(params.get('transactions_updated') || 0);
      if (imported || updated) {
        toast.success(`Bankkoppeling gelukt. ${imported} nieuwe transacties en ${updated} bijgewerkte transacties.`);
      } else {
        toast.success('Bankkoppeling gelukt');
      }
      loadAccounts();
      loadBankLinkStatus();
    }
  }, [location.search]);

  const handleUpgrade = async () => {
    await upgradeToPremium();
    setPremiumOpen(false);
  };

  const handleNotificationChange = (key) => {
    setNotifications(prev => ({ ...prev, [key]: !prev[key] }));
    toast.success('Voorkeuren opgeslagen');
  };

  const saveFamilyProfile = async () => {
    try {
      const years = childrenYearsInput
        .split(',')
        .map((v) => v.trim())
        .filter(Boolean)
        .map((v) => Number(v))
        .filter((v) => Number.isInteger(v) && v > 1900);

      const payload = {
        ...familyProfile,
        children_birth_years: years,
        children_count: Number(familyProfile.children_count || 0),
        kinderbijslag_quarterly: Number(familyProfile.kinderbijslag_quarterly || 0),
        kindgebonden_budget_monthly: Number(familyProfile.kindgebonden_budget_monthly || 0),
        kinderopvangtoeslag_monthly: Number(familyProfile.kinderopvangtoeslag_monthly || 0),
        zorgtoeslag_monthly: Number(familyProfile.zorgtoeslag_monthly || 0),
        partner_income_monthly: Number(familyProfile.partner_income_monthly || 0),
        household_taxable_income_annual: Number(familyProfile.household_taxable_income_annual || 0),
        net_salary_monthly: Number(familyProfile.net_salary_monthly || 0),
        income_type: familyProfile.income_type || 'salary',
        payment_frequency: familyProfile.payment_frequency || 'monthly',
        expected_payment_day: Number(familyProfile.expected_payment_day || 25),
        holiday_allowance_month: Number(familyProfile.holiday_allowance_month || 5),
        thirteenth_month_month: Number(familyProfile.thirteenth_month_month || 12),
        holiday_allowance_percent: Number(familyProfile.holiday_allowance_percent || 0),
        has_thirteenth_month: Boolean(familyProfile.has_thirteenth_month),
        thirteenth_month_amount: Number(familyProfile.thirteenth_month_amount || 0),
        fixed_allowances_monthly: Number(familyProfile.fixed_allowances_monthly || 0),
        fixed_deductions_monthly: Number(familyProfile.fixed_deductions_monthly || 0),
        bike_plan_monthly: Number(familyProfile.bike_plan_monthly || 0),
        self_employed_tax_reserve_percent: Number(familyProfile.self_employed_tax_reserve_percent || 0),
        self_employed_vat_reserve_percent: Number(familyProfile.self_employed_vat_reserve_percent || 0),
        self_employed_buffer_percent: Number(familyProfile.self_employed_buffer_percent || 0),
        vacation_days_per_year: Number(familyProfile.vacation_days_per_year || 0),
        vacation_days_remaining: Number(familyProfile.vacation_days_remaining || 0),
      };
      const res = await apiClient.put('/family/profile', payload);
      setFamilyProfile(res.data);
      setChildrenYearsInput((res.data.children_birth_years || []).join(', '));
      toast.success('Gezinsprofiel opgeslagen');
    } catch (error) {
      console.error('Error saving family profile:', error);
      toast.error('Opslaan van je gezinsgegevens is niet gelukt');
    }
  };

  const runIncomeScenario = async (type) => {
    try {
      const res = await apiClient.post(`/scenario/simulate?scenario_type=${type}&percentage=${scenarioPercent}`);
      setScenarioResult(res.data);
    } catch (error) {
      console.error('Error running scenario:', error);
      toast.error('De berekening is niet gelukt');
    }
  };

  const saveActiveRulesYear = async () => {
    try {
      const res = await apiClient.put('/family/rules-active-year', { active_year: rulesMeta.active_year });
      setRulesMeta(res.data);
      toast.success('Regeljaar bijgewerkt');
    } catch (error) {
      console.error('Error saving rules year:', error);
      toast.error(error.response?.data?.detail || 'Bijwerken van het jaar is niet gelukt');
    }
  };

  const saveBudgets = async () => {
    try {
      setSavingBudgets(true);
      const payload = {
        budgets: {
          boodschappen: Number(monthlyBudgets.boodschappen || 0),
          kleding: Number(monthlyBudgets.kleding || 0),
          uit_eten: Number(monthlyBudgets.uit_eten || 0),
          vaste_lasten: Number(monthlyBudgets.vaste_lasten || 0),
          overig: Number(monthlyBudgets.overig || 0),
        },
      };
      const res = await apiClient.put('/budgets/current', payload);
      setBudgetMonth(res.data?.month || '');
      toast.success('Begroting opgeslagen');
    } catch (error) {
      console.error('Error saving budgets:', error);
      toast.error(error.response?.data?.detail || 'Opslaan van je begroting is niet gelukt');
    } finally {
      setSavingBudgets(false);
    }
  };

  const resetBudgetsToProposal = async () => {
    await loadBudgets();
    toast.success('Voorstel opnieuw geladen');
  };

  const formatSyncDate = (value) => {
    if (!value) return '-';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return '-';
    return parsed.toLocaleString('nl-NL');
  };

  const createAccount = async () => {
    if (!accountForm.bank_name.trim() || !accountForm.account_name.trim()) {
      toast.error('Vul banknaam en rekeningnaam in');
      return;
    }

    try {
      setSavingAccount(true);
      const payload = {
        bank_name: accountForm.bank_name.trim(),
        account_name: accountForm.account_name.trim(),
        iban: accountForm.iban.trim(),
        account_type: accountForm.account_type,
        balance: Number(accountForm.balance || 0),
      };
      const res = await apiClient.post('/accounts', payload);
      setBankAccounts((prev) => [res.data, ...prev]);
      setAccountForm({
        bank_name: '',
        account_name: '',
        iban: '',
        account_type: 'checking',
        balance: '0',
      });
      toast.success('Bankrekening toegevoegd');
    } catch (error) {
      console.error('Error creating account:', error);
      toast.error(error.response?.data?.detail || 'Toevoegen van de bankrekening is niet gelukt');
    } finally {
      setSavingAccount(false);
    }
  };

  const deleteAccount = async (accountId) => {
    if (!window.confirm('Bankrekening verwijderen?')) return;
    try {
      await apiClient.delete(`/accounts/${accountId}`);
      setBankAccounts((prev) => prev.filter((acc) => acc.id !== accountId));
      toast.success('Bankrekening verwijderd');
    } catch (error) {
      console.error('Error deleting account:', error);
      toast.error(error.response?.data?.detail || 'Verwijderen van de bankrekening is niet gelukt');
    }
  };

  const startAutomaticBankLink = async () => {
    try {
      setStartingBankLink(true);
      const res = await apiClient.post('/banking/truelayer/start');
      if (!res.data?.auth_url) {
        throw new Error('Geen koppellink ontvangen');
      }
      window.location.href = res.data.auth_url;
    } catch (error) {
      console.error('Error starting bank link:', error);
      toast.error(error.response?.data?.detail || 'Starten van de bankkoppeling is niet gelukt');
    } finally {
      setStartingBankLink(false);
    }
  };

  const syncAutomaticBankLink = async () => {
    try {
      setSyncingBankLink(true);
      const res = await apiClient.post('/banking/truelayer/sync');
      const imported = Number(res.data?.transactions_imported || 0);
      const updated = Number(res.data?.transactions_updated || 0);
      await Promise.all([loadAccounts(), loadBankLinkStatus()]);
      toast.success(`Bankgegevens ververst. ${imported} nieuwe transacties en ${updated} bijgewerkte transacties.`);
    } catch (error) {
      console.error('Error syncing bank link:', error);
      toast.error(error.response?.data?.detail || 'Verversen van de bankkoppeling is niet gelukt');
    } finally {
      setSyncingBankLink(false);
    }
  };

  const resetTransactions = async (scope) => {
    const isAll = scope === 'all';
    const confirmed = window.confirm(
      isAll
        ? 'Weet je zeker dat je alle transacties wilt verwijderen? Dit is bedoeld als schone herstart.'
        : 'Weet je zeker dat je alle CSV-import transacties wilt verwijderen zodat je opnieuw schoon kunt importeren?'
    );
    if (!confirmed) return;

    try {
      if (isAll) {
        setResettingAllTransactions(true);
      } else {
        setResettingCsvImports(true);
      }
      const res = await apiClient.post('/transactions/reset', { scope });
      const { deleted_transactions = 0, deleted_accounts = 0, reset_accounts = 0 } = res.data || {};
      toast.success(
        `${deleted_transactions} transacties verwijderd, ${deleted_accounts} importrekeningen verwijderd, ${reset_accounts} rekeningen bijgewerkt`
      );
      await Promise.all([loadAccounts(), loadBankLinkStatus()]);
    } catch (error) {
      console.error('Error resetting transactions:', error);
      toast.error(error.response?.data?.detail || 'Opschonen mislukt');
    } finally {
      if (isAll) {
        setResettingAllTransactions(false);
      } else {
        setResettingCsvImports(false);
      }
    }
  };

  const handleDeleteAccount = async () => {
    if (!deleteAccountForm.password.trim()) {
      toast.error('Vul eerst je wachtwoord in');
      return;
    }
    if (deleteAccountForm.confirmation.trim().toUpperCase() !== 'VERWIJDER') {
      toast.error('Typ VERWIJDER om door te gaan');
      return;
    }

    try {
      setDeletingAccount(true);
      await apiClient.post('/auth/delete-account', {
        password: deleteAccountForm.password,
        confirmation: deleteAccountForm.confirmation,
      });
      setDeleteAccountOpen(false);
      setDeleteAccountForm({ password: '', confirmation: '' });
      toast.success('Je account is definitief verwijderd');
      logout();
    } catch (error) {
      console.error('Error deleting account:', error);
      toast.error(error.response?.data?.detail || 'Verwijderen van je account is niet gelukt');
    } finally {
      setDeletingAccount(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="settings-page">
      <AppPageIntro
        eyebrow="Account en voorkeuren"
        title="Instellingen"
        description="Beheer hier je profiel, rekeningen, meldingen en premium-onderdelen in dezelfde compacte app-structuur als de rest van budgettoppers."
        badges={['Profiel', 'Rekeningen', 'Voorkeuren']}
        icon={User}
        action={
          <div className="flex flex-wrap gap-2">
            <Button
              className="rounded-full"
              onClick={() => document.getElementById('accounts-card')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
            >
              Naar rekeningen
            </Button>
            <Button
              className="rounded-full"
              variant="outline"
              onClick={() => document.getElementById('profile-card')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
            >
              Profiel beheren
            </Button>
          </div>
        }
      />

      <Card className="shadow-card border-primary/20 bg-primary/5">
        <CardHeader>
          <CardTitle className="font-heading">Profiel en modules</CardTitle>
          <CardDescription>Kies waarvoor je budgettoppers gebruikt. Kerkgerichte financiële onderdelen worden zichtbaar in de kerkmodus.</CardDescription>
        </CardHeader>
        <CardContent className="flex items-center justify-between gap-4">
          <div><p className="font-medium">Kerkmodus</p><p className="text-sm text-muted-foreground">Collectes, giften en maandelijkse financiële verantwoording.</p></div>
          <Switch checked={churchMode} onCheckedChange={toggleChurchMode} aria-label="Kerkmodus" />
        </CardContent>
      </Card>

      {showSetupCard && (
        <Card className="shadow-card border-primary/15 bg-primary/5">
          <CardHeader>
            <CardTitle className="font-heading flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-primary" />
              Eerste inrichting
            </CardTitle>
            <CardDescription>
              Rond deze onderdelen af om sneller bruikbare inzichten uit budgettoppers te halen.
            </CardDescription>
          </CardHeader>
          <CardContent className="grid gap-3 md:grid-cols-3">
            {onboardingItems.map((item) => (
              <div key={item.key} className="rounded-xl border bg-background p-4">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="font-medium">{item.title}</p>
                    <p className="mt-1 text-sm text-muted-foreground">{item.description}</p>
                  </div>
                  <Badge variant={item.done ? 'default' : 'secondary'} className={item.done ? 'bg-green-100 text-green-700' : ''}>
                    {item.done ? 'Klaar' : 'Open'}
                  </Badge>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Settings */}
        <div className="lg:col-span-2 space-y-6">
          {/* Profile */}
          <Card className="shadow-card" id="profile-card">
            <CardHeader>
              <CardTitle className="font-heading flex items-center gap-2">
                <User className="w-5 h-5" />
                Profiel
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
                  <span className="font-heading text-2xl font-bold text-primary">
                    {user?.name?.charAt(0).toUpperCase()}
                  </span>
                </div>
                <div>
                  <p className="font-semibold text-lg">{user?.name}</p>
                  <p className="text-muted-foreground">{user?.email}</p>
                  {user?.is_premium && (
                    <Badge className="mt-1 bg-secondary text-secondary-foreground">
                      <Crown className="w-3 h-3 mr-1" />
                      Premium
                    </Badge>
                  )}
                </div>
              </div>
              <Separator />
              <div className="grid gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Naam</Label>
                  <Input id="name" defaultValue={user?.name} disabled />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">E-mail</Label>
                  <Input id="email" type="email" defaultValue={user?.email} disabled />
                </div>
              </div>
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                Profiel bewerken komt binnenkort beschikbaar. Je huidige naam en e-mailadres zijn hier alvast zichtbaar.
              </div>
              <Button className="rounded-full" disabled>
                Binnenkort beschikbaar
              </Button>
            </CardContent>
          </Card>

          {/* Bank Accounts */}
          <Card className="shadow-card" id="accounts-card">
            <CardHeader>
              <CardTitle className="font-heading flex items-center gap-2">
                <Wallet className="w-5 h-5" />
                Bankrekeningen
              </CardTitle>
              <CardDescription>
                Voeg handmatig je bankrekening toe met saldo en type
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="rounded-xl border border-primary/15 bg-primary/5 p-4">
                <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                  <div>
                    <p className="font-medium flex items-center gap-2">
                      <Link2 className="w-4 h-4 text-primary" />
                      Koppel je bank automatisch
                    </p>
                    <p className="mt-1 text-sm text-muted-foreground">
                      Verbind je rekening via TrueLayer en haal je rekeninggegevens automatisch op.
                    </p>
                    <p className="mt-2 text-xs text-muted-foreground">
                      Status: {bankLinkStatus.configured ? `klaar voor ${bankLinkStatus.mode || 'sandbox'}` : 'nog niet klaar'}
                      {bankLinkStatus.connected_accounts ? ` · ${bankLinkStatus.connected_accounts} gekoppeld` : ''}
                      {bankLinkStatus.last_transactions_sync_at ? ` · laatst ververst ${formatSyncDate(bankLinkStatus.last_transactions_sync_at)}` : ''}
                    </p>
                  </div>
                  <div className="flex flex-col gap-2 sm:flex-row">
                    {bankLinkStatus.connected_accounts > 0 && (
                      <Button
                        onClick={syncAutomaticBankLink}
                        variant="outline"
                        className="rounded-full"
                        disabled={syncingBankLink}
                      >
                        {syncingBankLink ? 'Bezig...' : 'Transacties verversen'}
                      </Button>
                    )}
                    <Button
                      onClick={startAutomaticBankLink}
                      className="rounded-full"
                      disabled={!bankLinkStatus.configured || startingBankLink}
                    >
                      {startingBankLink ? 'Bezig...' : 'Bank automatisch koppelen'}
                    </Button>
                  </div>
                </div>
                {!bankLinkStatus.configured && (
                  <p className="mt-3 text-sm text-amber-700">
                    De server mist nog TrueLayer-gegevens. Zet eerst de juiste backend instellingen klaar.
                  </p>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Banknaam</Label>
                  <Input
                    placeholder="Bijv. ING"
                    value={accountForm.bank_name}
                    onChange={(e) => setAccountForm((p) => ({ ...p, bank_name: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Rekeningnaam</Label>
                  <Input
                    placeholder="Bijv. Betaalrekening"
                    value={accountForm.account_name}
                    onChange={(e) => setAccountForm((p) => ({ ...p, account_name: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>IBAN</Label>
                  <Input
                    placeholder="Bijv. NL91INGB0001234567"
                    value={accountForm.iban}
                    onChange={(e) => setAccountForm((p) => ({ ...p, iban: e.target.value.toUpperCase() }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Type rekening</Label>
                  <Select
                    value={accountForm.account_type}
                    onValueChange={(value) => setAccountForm((p) => ({ ...p, account_type: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Kies type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="checking">Betaalrekening</SelectItem>
                      <SelectItem value="savings">Spaarrekening</SelectItem>
                      <SelectItem value="credit">Creditcard</SelectItem>
                      <SelectItem value="investment">Beleggingsrekening</SelectItem>
                      <SelectItem value="other">Overig</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Saldo (€)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={accountForm.balance}
                    onChange={(e) => setAccountForm((p) => ({ ...p, balance: e.target.value }))}
                  />
                </div>
              </div>
              <Button onClick={createAccount} className="rounded-full" disabled={savingAccount}>
                {savingAccount ? 'Opslaan...' : 'Bankrekening toevoegen'}
              </Button>

              <Separator />
              {bankAccounts.length === 0 && (
                <p className="text-sm text-muted-foreground">Nog geen bankrekeningen toegevoegd.</p>
              )}
              {bankAccounts.map((bank) => (
                <div
                  key={bank.id}
                  className="flex items-center justify-between p-4 rounded-xl border border-border"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-muted">
                      <Wallet className="w-5 h-5 text-muted-foreground" />
                    </div>
                    <div>
                      <p className="font-medium">{bank.bank_name} - {bank.account_name}</p>
                      <p className="text-xs text-muted-foreground">
                        Type: {bank.account_type}
                        {bank.iban ? ` · IBAN: ${bank.iban}` : ''}
                        {' · '}Laatste update: {formatSyncDate(bank.last_sync)}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline">€{Number(bank.balance || 0).toFixed(2)}</Badge>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="text-muted-foreground hover:text-red-600"
                      onClick={() => deleteAccount(bank.id)}
                      aria-label="Verwijder bankrekening"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>

          {/* Notifications */}
          <Card className="shadow-card">
            <CardHeader>
              <CardTitle className="font-heading flex items-center gap-2">
                <Bell className="w-5 h-5" />
                Notificaties
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {[
                { key: 'contractAlerts', label: 'Contract herinneringen', description: 'Ontvang meldingen wanneer contracten aflopen' },
                { key: 'savingsGoals', label: 'Spaardoel updates', description: 'Voortgang van je spaardoelen' },
                { key: 'weeklyReport', label: 'Wekelijks overzicht', description: 'Wekelijkse samenvatting van je financiën' },
                { key: 'priceDrops', label: 'Prijsdalingen', description: 'Meldingen bij goedkopere alternatieven' },
              ].map((item) => (
                <div key={item.key} className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">{item.label}</p>
                    <p className="text-sm text-muted-foreground">{item.description}</p>
                  </div>
                  <Switch
                    checked={notifications[item.key]}
                    onCheckedChange={() => handleNotificationChange(item.key)}
                  />
                </div>
              ))}
            </CardContent>
          </Card>

          <Card className="shadow-card border-amber-200 bg-amber-50/60">
            <CardHeader>
              <CardTitle className="font-heading flex items-center gap-2">
                <Trash2 className="w-5 h-5 text-amber-700" />
                Gegevens opschonen
              </CardTitle>
              <CardDescription>
                Alleen gebruiken als transacties verkeerd zijn geïmporteerd en je opnieuw schoon wilt beginnen.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="rounded-xl border border-amber-200 bg-background p-4 text-sm text-muted-foreground">
                Handig als transacties op de verkeerde rekening zijn beland of als je opnieuw wilt importeren zonder oude ballast.
              </div>
              <div className="flex flex-col gap-3 md:flex-row">
                <Button
                  type="button"
                  variant="outline"
                  className="rounded-full border-amber-300 bg-background"
                  onClick={() => resetTransactions('csv_imports')}
                  disabled={resettingCsvImports || resettingAllTransactions}
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  {resettingCsvImports ? 'CSV-imports verwijderen...' : 'Verwijder alleen CSV-imports'}
                </Button>
                <Button
                  type="button"
                  variant="destructive"
                  className="rounded-full"
                  onClick={() => resetTransactions('all')}
                  disabled={resettingCsvImports || resettingAllTransactions}
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  {resettingAllTransactions ? 'Alles verwijderen...' : 'Verwijder alle transacties'}
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card className="shadow-card">
            <CardHeader>
              <CardTitle className="font-heading flex items-center gap-2">
                <Briefcase className="w-5 h-5" />
                Werk & Inkomen
              </CardTitle>
              <CardDescription>
                Voor inzicht in vakantiegeld, 13e maand, vaste vergoedingen en vakantiedagen
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>{familyProfile.income_type === 'freelance' ? 'Verwachte omzet per maand (€)' : 'Netto salaris per maand (€)'}</Label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={familyProfile.net_salary_monthly}
                    onChange={(e) => setFamilyProfile((p) => ({ ...p, net_salary_monthly: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Soort inkomen</Label>
                  <Select
                    value={familyProfile.income_type || 'salary'}
                    onValueChange={(value) => setFamilyProfile((p) => ({ ...p, income_type: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Kies soort inkomen" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="salary">Loon</SelectItem>
                      <SelectItem value="benefit">Uitkering</SelectItem>
                      <SelectItem value="pension">Pensioen</SelectItem>
                      <SelectItem value="freelance">ZZP / freelance</SelectItem>
                      <SelectItem value="other">Overig</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Betaalritme</Label>
                  <Select
                    value={familyProfile.payment_frequency || 'monthly'}
                    onValueChange={(value) => setFamilyProfile((p) => ({ ...p, payment_frequency: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Kies ritme" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="monthly">Maandelijks</SelectItem>
                      <SelectItem value="4-weeks">Per 4 weken</SelectItem>
                      <SelectItem value="weekly">Wekelijks</SelectItem>
                      <SelectItem value="quarterly">Per kwartaal</SelectItem>
                      <SelectItem value="yearly">Jaarlijks</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Gewone betaaldag</Label>
                  <Input
                    type="number"
                    min="1"
                    max="31"
                    step="1"
                    value={familyProfile.expected_payment_day}
                    onChange={(e) => setFamilyProfile((p) => ({ ...p, expected_payment_day: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Vakantiegeld (%)</Label>
                  <Input
                    type="number"
                    min="0"
                    step="0.1"
                    value={familyProfile.holiday_allowance_percent}
                    onChange={(e) => setFamilyProfile((p) => ({ ...p, holiday_allowance_percent: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Maand van vakantiegeld</Label>
                  <Input
                    type="number"
                    min="1"
                    max="12"
                    step="1"
                    value={familyProfile.holiday_allowance_month}
                    onChange={(e) => setFamilyProfile((p) => ({ ...p, holiday_allowance_month: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Vaste vergoedingen per maand (€)</Label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={familyProfile.fixed_allowances_monthly}
                    onChange={(e) => setFamilyProfile((p) => ({ ...p, fixed_allowances_monthly: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Vaste inhoudingen per maand (€)</Label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={familyProfile.fixed_deductions_monthly}
                    onChange={(e) => setFamilyProfile((p) => ({ ...p, fixed_deductions_monthly: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Fietsplan of lease inhouding per maand (€)</Label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={familyProfile.bike_plan_monthly}
                    onChange={(e) => setFamilyProfile((p) => ({ ...p, bike_plan_monthly: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>13e maand bedrag (€)</Label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={familyProfile.thirteenth_month_amount}
                    onChange={(e) => setFamilyProfile((p) => ({ ...p, thirteenth_month_amount: e.target.value }))}
                    disabled={!familyProfile.has_thirteenth_month}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Maand van 13e maand</Label>
                  <Input
                    type="number"
                    min="1"
                    max="12"
                    step="1"
                    value={familyProfile.thirteenth_month_month}
                    onChange={(e) => setFamilyProfile((p) => ({ ...p, thirteenth_month_month: e.target.value }))}
                    disabled={!familyProfile.has_thirteenth_month}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Vakantiedagen per jaar</Label>
                  <Input
                    type="number"
                    min="0"
                    step="1"
                    value={familyProfile.vacation_days_per_year}
                    onChange={(e) => setFamilyProfile((p) => ({ ...p, vacation_days_per_year: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Nog openstaande vakantiedagen</Label>
                  <Input
                    type="number"
                    min="0"
                    step="1"
                    value={familyProfile.vacation_days_remaining}
                    onChange={(e) => setFamilyProfile((p) => ({ ...p, vacation_days_remaining: e.target.value }))}
                  />
                </div>
              </div>
              {familyProfile.income_type === 'freelance' && (
                <div className="space-y-4 rounded-xl border border-primary/15 bg-primary/5 p-4">
                  <div>
                    <p className="font-medium">ZZP reserve</p>
                    <p className="mt-1 text-sm text-muted-foreground">
                      Hiermee reken je van ontvangen omzet naar een veiliger besteedbaar bedrag.
                    </p>
                  </div>
                  <div className="rounded-lg border bg-background/80 p-3 text-sm text-muted-foreground">
                    Een eenvoudige start kan zijn:
                    {' '}`25% - 35%` voor inkomstenbelasting,
                    {' '}`21%` voor btw als je btw rekent,
                    {' '}en `5% - 10%` extra buffer.
                    {' '}Twijfel je? Neem dan je laatste aangifte, een voorlopige aanslag of je boekhouder als uitgangspunt.
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div className="space-y-2">
                      <Label>Reserve inkomstenbelasting (%)</Label>
                      <Input
                        type="number"
                        min="0"
                        max="100"
                        step="0.1"
                        value={familyProfile.self_employed_tax_reserve_percent}
                        onChange={(e) => setFamilyProfile((p) => ({ ...p, self_employed_tax_reserve_percent: e.target.value }))}
                      />
                      <p className="text-xs text-muted-foreground">
                        Zet hier het deel apart dat je later meestal aan belasting kwijt bent.
                      </p>
                    </div>
                    <div className="space-y-2">
                      <Label>Reserve btw (%)</Label>
                      <Input
                        type="number"
                        min="0"
                        max="100"
                        step="0.1"
                        value={familyProfile.self_employed_vat_reserve_percent}
                        onChange={(e) => setFamilyProfile((p) => ({ ...p, self_employed_vat_reserve_percent: e.target.value }))}
                      />
                      <p className="text-xs text-muted-foreground">
                        Vul alleen iets in als je btw ontvangt en dit niet direct apart zet op een andere rekening.
                      </p>
                    </div>
                    <div className="space-y-2">
                      <Label>Extra buffer (%)</Label>
                      <Input
                        type="number"
                        min="0"
                        max="100"
                        step="0.1"
                        value={familyProfile.self_employed_buffer_percent}
                        onChange={(e) => setFamilyProfile((p) => ({ ...p, self_employed_buffer_percent: e.target.value }))}
                      />
                      <p className="text-xs text-muted-foreground">
                        Handig voor rustigere maanden, correcties of onverwachte kosten.
                      </p>
                    </div>
                  </div>
                </div>
              )}
              <div className="flex items-center gap-2">
                <Switch
                  checked={Boolean(familyProfile.has_thirteenth_month)}
                  onCheckedChange={(checked) => setFamilyProfile((p) => ({
                    ...p,
                    has_thirteenth_month: checked,
                    thirteenth_month_amount: checked ? p.thirteenth_month_amount : 0,
                  }))}
                />
                <Label>Ik ontvang een 13e maand</Label>
              </div>
              <div className="rounded-lg border border-primary/15 bg-primary/5 p-4 text-sm text-muted-foreground">
                {familyProfile.income_type === 'freelance'
                  ? 'Vul hier je basisbedrag en reservepercentages in. Dan zie je op je dashboard beter wat ontvangen omzet is en wat je veiliger apart houdt.'
                  : 'Vul hier alleen de vaste onderdelen in. Dan kun je straks beter zien wat je extra ontvangt rond vakantiegeld, 13e maand en verlof.'}
              </div>
              <Button className="rounded-full" onClick={saveFamilyProfile}>
                Werk & inkomen opslaan
              </Button>
            </CardContent>
          </Card>

          <Card className="shadow-card">
            <CardHeader>
              <CardTitle className="font-heading flex items-center gap-2">
                <Baby className="w-5 h-5" />
                Gezin & Tegemoetkomingen
              </CardTitle>
              <CardDescription>
                Voor scenario's rond inkomenswijziging, kinderbijslag en kindgebonden budget
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Aantal kinderen</Label>
                  <Input
                    type="number"
                    min="0"
                    value={familyProfile.children_count}
                    onChange={(e) => setFamilyProfile((p) => ({ ...p, children_count: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Geboortejaren kinderen (komma gescheiden)</Label>
                  <Input
                    placeholder="2018, 2021"
                    value={childrenYearsInput}
                    onChange={(e) => setChildrenYearsInput(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Kinderbijslag per kwartaal (€)</Label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={familyProfile.kinderbijslag_quarterly}
                    onChange={(e) => setFamilyProfile((p) => ({ ...p, kinderbijslag_quarterly: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Kindgebonden budget per maand (€)</Label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={familyProfile.kindgebonden_budget_monthly}
                    onChange={(e) => setFamilyProfile((p) => ({ ...p, kindgebonden_budget_monthly: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Kinderopvangtoeslag per maand (€)</Label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={familyProfile.kinderopvangtoeslag_monthly}
                    onChange={(e) => setFamilyProfile((p) => ({ ...p, kinderopvangtoeslag_monthly: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Zorgtoeslag per maand (€)</Label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={familyProfile.zorgtoeslag_monthly}
                    onChange={(e) => setFamilyProfile((p) => ({ ...p, zorgtoeslag_monthly: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Partnerinkomen per maand (€)</Label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={familyProfile.partner_income_monthly}
                    onChange={(e) => setFamilyProfile((p) => ({ ...p, partner_income_monthly: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Toetsingsinkomen huishouden per jaar (€)</Label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={familyProfile.household_taxable_income_annual}
                    onChange={(e) => setFamilyProfile((p) => ({ ...p, household_taxable_income_annual: e.target.value }))}
                  />
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={Boolean(familyProfile.single_parent_household)}
                  onCheckedChange={(checked) => setFamilyProfile((p) => ({ ...p, single_parent_household: checked }))}
                />
                <Label>Alleenstaande ouder huishouden</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={Boolean(familyProfile.receives_kinderbijslag)}
                  onCheckedChange={(checked) => setFamilyProfile((p) => ({ ...p, receives_kinderbijslag: checked }))}
                />
                <Label>Ontvangt kinderbijslag</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={Boolean(familyProfile.receives_kindgebonden_budget)}
                  onCheckedChange={(checked) => setFamilyProfile((p) => ({ ...p, receives_kindgebonden_budget: checked }))}
                />
                <Label>Ontvangt kindgebonden budget</Label>
              </div>
              <Button className="rounded-full" onClick={saveFamilyProfile}>
                Gegevens opslaan
              </Button>

              <Separator />
              <div className="space-y-2">
                <Label>Actief regeljaar tegemoetkomingen</Label>
                <div className="flex flex-col md:flex-row gap-2">
                  <Select
                    value={rulesMeta.active_year || ''}
                    onValueChange={(value) => setRulesMeta((p) => ({ ...p, active_year: value }))}
                  >
                    <SelectTrigger className="md:w-56">
                      <SelectValue placeholder="Kies jaar" />
                    </SelectTrigger>
                    <SelectContent>
                      {rulesMeta.available_years.map((year) => (
                        <SelectItem key={year} value={year}>{year}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button variant="outline" onClick={saveActiveRulesYear}>
                    Regeljaar opslaan
                  </Button>
                </div>
                {rulesMeta.rule_version && (
                  <p className="text-xs text-muted-foreground">Actieve set: {rulesMeta.rule_version}</p>
                )}
              </div>
            </CardContent>
          </Card>

          <Card className="shadow-card">
            <CardHeader>
              <CardTitle className="font-heading flex items-center gap-2">
                <Wallet className="w-5 h-5" />
                Budgetten
              </CardTitle>
              <CardDescription>
                Stel per maand een eenvoudig budget in voor je belangrijkste categorieën.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="rounded-lg border border-primary/15 bg-primary/5 p-4 text-sm text-muted-foreground">
                {budgetMonth
                  ? `Begroting voor ${budgetMonth}`
                  : 'Je ziet hier het voorstel voor deze maand. Pas het gerust aan op jouw situatie.'}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {[
                  { key: 'boodschappen', label: 'Boodschappen' },
                  { key: 'kleding', label: 'Kleding' },
                  { key: 'uit_eten', label: 'Uit eten' },
                  { key: 'vaste_lasten', label: 'Vaste lasten' },
                  { key: 'overig', label: 'Overig' },
                ].map((item) => (
                  <div key={item.key} className="space-y-2">
                    <Label>{item.label}</Label>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={monthlyBudgets[item.key]}
                      onChange={(e) => setMonthlyBudgets((prev) => ({ ...prev, [item.key]: e.target.value }))}
                    />
                  </div>
                ))}
              </div>
              <div className="flex flex-col gap-2 md:flex-row">
                <Button className="rounded-full" onClick={saveBudgets} disabled={savingBudgets}>
                  {savingBudgets ? 'Opslaan...' : 'Opslaan'}
                </Button>
                <Button variant="outline" className="rounded-full" onClick={resetBudgetsToProposal}>
                  Reset naar voorstel
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card className="shadow-card">
            <CardHeader>
              <CardTitle className="font-heading flex items-center gap-2">
                <TrendingUp className="w-5 h-5" />
                Inkomenswijziging (incl. tegemoetkomingen)
              </CardTitle>
              <CardDescription>
                Simuleert salariswijziging met effect op kindgebonden budget/toeslagen
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
                <div className="space-y-2 sm:max-w-[120px]">
                  <Label>Percentage</Label>
                  <Input
                    type="number"
                    min="1"
                    max="80"
                    value={scenarioPercent}
                    onChange={(e) => setScenarioPercent(Number(e.target.value))}
                  />
                </div>
                <Button className="w-full sm:w-auto" variant="outline" onClick={() => runIncomeScenario('income_decrease')}>
                  Inkomensdaling
                </Button>
                <Button className="w-full sm:w-auto" onClick={() => runIncomeScenario('income_increase')}>
                  Inkomensstijging
                </Button>
              </div>
              {scenarioResult && (
                <div className="rounded-lg border p-3 text-sm">
                  <p className="font-medium">{scenarioResult.scenario_name}</p>
                  <p>Huidig: €{scenarioResult.current_monthly?.toFixed(2)} /mnd</p>
                  <p>Nieuw: €{scenarioResult.projected_monthly?.toFixed(2)} /mnd</p>
                  <p className={scenarioResult.impact >= 0 ? 'text-green-600' : 'text-red-600'}>
                    Impact: €{scenarioResult.impact?.toFixed(2)} /mnd
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Plan Info */}
          <Card className={`shadow-card ${user?.is_premium ? 'border-secondary/50 bg-secondary/5' : ''}`}>
            <CardHeader>
              <CardTitle className="font-heading flex items-center gap-2">
                <CreditCard className="w-5 h-5" />
                Abonnement
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-center py-4">
                {user?.is_premium ? (
                  <>
                    <Crown className="w-12 h-12 mx-auto text-secondary mb-3" />
                    <h3 className="font-heading font-bold text-xl">Premium</h3>
                    <p className="text-muted-foreground text-sm mt-1">€3,99/maand</p>
                    <Badge className="mt-2 bg-green-100 text-green-700">Actief</Badge>
                  </>
                ) : (
                  <>
                    <Wallet className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
                    <h3 className="font-heading font-bold text-xl">Gratis</h3>
                    <p className="text-muted-foreground text-sm mt-1">De basis van budgettoppers</p>
                    <Dialog open={premiumOpen} onOpenChange={setPremiumOpen}>
                      <DialogTrigger asChild>
                        <Button className="mt-4 rounded-full w-full bg-primary hover:bg-primary/90">
                          Upgrade naar Premium
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle className="font-heading flex items-center gap-2">
                            <Crown className="w-6 h-6 text-secondary" />
                            budgettoppers Premium
                          </DialogTitle>
                          <DialogDescription>
                            Krijg meer overzicht en extra hulp in de app
                          </DialogDescription>
                        </DialogHeader>
                        <div className="space-y-4 py-4">
                          <ul className="space-y-3">
                            {[
                              'Meer rekeningen toevoegen',
                              'Extra hulp bij energie en internet',
                              'Bijlagen bij contracten bewaren',
                              'Meer inzicht in wat-als situaties'
                            ].map((feature, i) => (
                              <li key={i} className="flex items-center gap-3">
                                <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center">
                                  <Check className="w-4 h-4 text-primary" />
                                </div>
                                <span>{feature}</span>
                              </li>
                            ))}
                          </ul>
                          <div className="pt-4 border-t">
                            <div className="flex items-baseline gap-2 mb-4">
                              <span className="text-3xl font-bold font-heading">€3,99</span>
                              <span className="text-muted-foreground">/maand</span>
                            </div>
                            <Button onClick={handleUpgrade} className="w-full rounded-full bg-primary hover:bg-primary/90">
                              Kies Premium
                            </Button>
                          </div>
                        </div>
                      </DialogContent>
                    </Dialog>
                  </>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Security */}
          <Card className="shadow-card">
            <CardHeader>
              <CardTitle className="font-heading flex items-center gap-2">
                <Shield className="w-5 h-5" />
                Beveiliging
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                Wachtwoord wijzigen en tweefactorauthenticatie worden nog verder uitgewerkt. Account verwijderen is wel al volledig vanuit de app te starten.
              </div>
              <Button variant="outline" className="w-full justify-start" disabled>
                Wachtwoord wijzigen
              </Button>
              <Button variant="outline" className="w-full justify-start" disabled>
                Tweefactorauthenticatie
              </Button>
              <Dialog open={deleteAccountOpen} onOpenChange={(open) => {
                setDeleteAccountOpen(open);
                if (!open) {
                  setDeleteAccountForm({ password: '', confirmation: '' });
                }
              }}>
                <DialogTrigger asChild>
                  <Button variant="outline" className="w-full justify-start text-red-600 hover:text-red-700 hover:bg-red-50">
                    Account verwijderen
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-md">
                  <DialogHeader>
                    <DialogTitle>Account definitief verwijderen</DialogTitle>
                    <DialogDescription>
                      Dit verwijdert je account en bijbehorende gegevens zoals transacties, rekeningen, contracten, boodschappendata en geüploade documenten. Deze actie kun je niet ongedaan maken.
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                      Typ <span className="font-semibold">VERWIJDER</span> en vul je wachtwoord in om door te gaan.
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="delete-account-password">Wachtwoord</Label>
                      <Input
                        id="delete-account-password"
                        type="password"
                        autoComplete="current-password"
                        value={deleteAccountForm.password}
                        onChange={(e) => setDeleteAccountForm((prev) => ({ ...prev, password: e.target.value }))}
                        placeholder="Je huidige wachtwoord"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="delete-account-confirmation">Typ VERWIJDER</Label>
                      <Input
                        id="delete-account-confirmation"
                        value={deleteAccountForm.confirmation}
                        onChange={(e) => setDeleteAccountForm((prev) => ({ ...prev, confirmation: e.target.value }))}
                        placeholder="VERWIJDER"
                        autoCapitalize="characters"
                        autoCorrect="off"
                        spellCheck={false}
                      />
                    </div>
                    <div className="flex flex-col gap-3 sm:flex-row">
                      <Button
                        type="button"
                        variant="outline"
                        className="w-full rounded-full"
                        onClick={() => setDeleteAccountOpen(false)}
                        disabled={deletingAccount}
                      >
                        Annuleren
                      </Button>
                      <Button
                        type="button"
                        variant="destructive"
                        className="w-full rounded-full"
                        onClick={handleDeleteAccount}
                        disabled={deletingAccount}
                      >
                        {deletingAccount ? 'Bezig met verwijderen...' : 'Definitief verwijderen'}
                      </Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            </CardContent>
          </Card>

          {/* Support */}
          <Card className="shadow-card">
            <CardContent className="p-6 text-center">
              <p className="text-sm text-muted-foreground mb-3">Hulp nodig?</p>
              <Button
                variant="outline"
                className="w-full rounded-full"
                onClick={() => window.location.href = 'mailto:budgettoppers@buybeez.nl?subject=Support%20budgettoppers'}
              >
                Contact Support
                <ExternalLink className="w-4 h-4 ml-2" />
              </Button>
            </CardContent>
          </Card>

          <Card className="shadow-card">
            <CardContent className="p-6">
              <Button
                variant="outline"
                className="w-full rounded-full border-red-200 text-red-600 hover:border-red-300 hover:bg-red-50 hover:text-red-700"
                onClick={logout}
              >
                Uitloggen
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
